import { ReactNode, useEffect, useState } from 'react';
import {
  DocumentIcon,
  LinkIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { RegisterOptions, useForm } from 'react-hook-form';
import { AnimatePresence, motion } from 'motion/react';

import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { ResourceFields } from '@/data/services/_deprecated/resourceServices';
import {
  ResourceFormProps,
  ResourceTypeEnum,
} from '@/models/_deprecated/Resource';
import TextInput from '@/components/ui/custom/dataInput/TextInput';
import SaveCancelGroup from '@/components/ui/custom/buttons/SaveCancelGroup';
import { validateLink } from '@/utils/validators/regex';
import User from '@/models/User';
import ResourceFileUpload from './ResourceFileUpload';
import { slideHorizontal } from '@/utils/animations/commom';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';

type DynamicResourceFormProps = {
  user: User;
  createResource(body: Partial<ResourceFields>): Promise<void>;
  loading?: boolean;
  editingAllowed?: boolean;
};

export default function DynamicResourceForm({
  user,
  createResource,
  editingAllowed,
  loading,
}: DynamicResourceFormProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const { id: authorId } = user;

  const [resourceType, setResourceType] = useState<ResourceTypeEnum>(
    ResourceTypeEnum.NONE,
  );

  const onSelectResource = (resourceType: ResourceTypeEnum) => {
    setResourceType(resourceType);
  };

  const onCancel = () => {
    setResourceType(ResourceTypeEnum.NONE);
  };

  const onSubmit = async (fields: ResourceFields) => {
    const body = {
      ...fields,
      authorId,
      title: fields.title || t('newResource'),
    };

    createResource(body);

    onCancel();
  };

  const resourceForms: {
    [key in ResourceTypeEnum]: ReactNode | null;
  } = {
    LINK: (
      <ResourceLinkForm
        onSubmit={onSubmit}
        onCancel={onCancel}
        loading={loading}
      />
    ),
    FILE: (
      <ResourceFileForm
        onSubmit={onSubmit}
        onCancel={onCancel}
        loading={loading}
      />
    ),
    NONE: null,
  };

  return (
    <section className="flex flex-col gap-3.5">
      <ConditionalRenderer condition={resourceType === ResourceTypeEnum.NONE}>
        <ResourceSelector
          onSelectResource={onSelectResource}
          editingAllowed={editingAllowed}
          loading={loading}
        />
      </ConditionalRenderer>
      {resourceForms[resourceType]}
    </section>
  );
}

type ResourceSelectorProps = {
  onSelectResource(resourceType?: ResourceTypeEnum): void;
  editingAllowed?: boolean;
  loading?: boolean;
};

function ResourceSelector({
  onSelectResource,
  editingAllowed,
  loading,
}: ResourceSelectorProps) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const [addingResource, setAddingResource] = useState(false);

  const handleAddingResource = () => {
    setAddingResource(prev => !prev);
    onSelectResource(ResourceTypeEnum.NONE);
  };

  const buttonClassName = 'font-500';

  const { t: editRulesT } = useTranslation('translation', {
    keyPrefix: 'adminPage.editCourseRules',
  });

  return (
    <section className="flex gap-2.5">
      <ConditionalWrapper
        condition={!editingAllowed}
        wrapper={
          <Tooltip
            text={editRulesT('cantModify')}
            classNameContainer="shrink-0"
          />
        }
      >
        <MainButton
          dataTestId={
            addingResource ? 'cancelResourceButton' : 'addResourceButton'
          }
          color={addingResource ? 'neutral' : 'primary'}
          className={buttonClassName}
          text={addingResource ? t('cancel') : t('newResource')}
          onClick={handleAddingResource}
          icon={addingResource ? <XMarkIcon /> : <PlusIcon />}
          disabled={!editingAllowed || loading}
        />
      </ConditionalWrapper>

      <div className="relative overflow-hidden w-full">
        <AnimatePresence>
          {addingResource && (
            <motion.div
              {...slideHorizontal(-200)}
              className="flex gap-2.5 overflow-hidden"
            >
              <MainButton
                dataTestId="resourceLinkButton"
                className={buttonClassName}
                text={t('link')}
                onClick={() => onSelectResource(ResourceTypeEnum.LINK)}
                icon={<LinkIcon />}
                disabled={!editingAllowed}
              />
              <MainButton
                dataTestId="resourceFileButton"
                className={buttonClassName}
                icon={<DocumentIcon />}
                text={t('file')}
                onClick={() => onSelectResource(ResourceTypeEnum.FILE)}
                disabled={!editingAllowed}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}

export function ResourceLinkForm({
  loading,
  onSubmit,
  onCancel,
}: Omit<ResourceFormProps, 'resourceType'>) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const {
    watch,
    handleSubmit,
    register,
    formState: { errors, defaultValues },
  } = useForm<ResourceFields>();

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const subscribe = watch(value =>
      setHasChanges(!isEqual(value, defaultValues)),
    );
    return () => {
      subscribe.unsubscribe();
    };
  }, [defaultValues, watch]);

  const resourceLinkMessage = errors.resourceLink?.message;

  const resourceTitleMessage = errors.title?.message;

  const validation: RegisterOptions<ResourceFields, 'resourceLink'> = {
    required: t('form.required', {
      field: 'link',
    }),
    pattern: {
      value: validateLink,
      message: t('form.invalid', { field: 'link' }),
    },
    validate: (value?: string | null) => {
      if (!value?.trim()) return t('form.empty', { field: 'link' });

      return true;
    },
  };

  return (
    <form className="flex flex-col gap-2.5" onSubmit={handleSubmit(onSubmit)}>
      <TextInput
        testId="resourceTitleInput"
        register={register('title')}
        label={t('title')}
        labelPosition="left"
        className={{ label: 'w-fit' }}
        errorLabelText={resourceTitleMessage}
        color={resourceTitleMessage ? 'warning' : 'primary'}
      />

      <TextInput
        testId="resourceLinkInput"
        register={register('resourceLink', validation)}
        label="Link"
        labelPosition="left"
        className={{ label: 'w-9' }}
        icon={<LinkIcon />}
        errorLabelText={resourceLinkMessage}
        color={resourceLinkMessage ? 'warning' : 'primary'}
      />

      <SaveCancelGroup
        className="font-500"
        loading={loading}
        cancel={{
          onClick: onCancel,
          testId: 'cancelButton',
        }}
        save={{
          testId: 'saveButton',
          disabled: !hasChanges || !!resourceLinkMessage,
        }}
      />
    </form>
  );
}

export function ResourceFileForm({
  onSubmit,
  onCancel,
  loading,
}: Omit<ResourceFormProps, 'resourceType'>) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const {
    watch,
    register,
    handleSubmit,
    setValue,
    formState: { errors, defaultValues },
  } = useForm<ResourceFields>();

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const subscribe = watch(value =>
      setHasChanges(!isEqual(value, defaultValues)),
    );
    return () => {
      subscribe.unsubscribe();
    };
  }, [defaultValues, watch]);

  const resourceTitleMessage = errors.title?.message;

  return (
    <form className="flex flex-col gap-2.5" onSubmit={handleSubmit(onSubmit)}>
      <TextInput
        testId="resourceTitleInput"
        register={register('title')}
        label={t('title')}
        labelPosition="left"
        className={{ label: 'w-fit' }}
        errorLabelText={resourceTitleMessage}
        color={resourceTitleMessage ? 'warning' : 'primary'}
      />

      <ResourceFileUpload
        testId="resourceFileInput"
        onChange={resourceFile => setValue('resourceFile', resourceFile)}
      />

      <SaveCancelGroup
        className="font-500"
        loading={loading}
        cancel={{
          onClick: onCancel,
          testId: 'cancelButton',
        }}
        save={{
          testId: 'saveButton',
          disabled: !hasChanges,
        }}
      />
    </form>
  );
}
